import { CART_COOKIE, GIFT_CARD_COOKIE } from "@/config/cart";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { getLink } from "@/config/market-configurations";
import type { MarketConfiguration } from "@/config/markets/const";
import { getCartCookie } from "@/utils/cart/getCartCookie";
import { COOKIE_MAX_AGE } from "@/utils/const";
import type { Cart, StockWatchQueryVariables } from "@server/gql/graphql";
import { createNewCart } from "@server/requests/checkout/createNewCart";
import { getCart } from "@server/requests/checkout/getCart";
import { deleteCookie, hasCookie, setCookie } from "cookies-next";
import { z } from "zod";
import { CHECKOUT_STORAGE_KEY } from "./Checkout.store";
import type { CheckoutLocalStorage } from "./Checkout.types";

export function getCheckoutRedirectUrlByMarket(
  currentMarket: MarketConfiguration,
  targetMarket: MarketConfiguration,
) {
  return getLink(currentMarket, targetMarket) + "/checkout";
}

/**
 * Update market and language cookies
 * This is used when changing market in the checkout
 * @param market - Market code
 * @param language - Language code
 */
export function updateMarketAndLanguageCookie({
  market,
  language,
}: {
  market: string;
  language: string;
}) {
  if (hasCookie("ng_suggestion")) {
    deleteCookie("ng_suggestion");
  }

  if (hasCookie("ng_dismissed")) {
    deleteCookie("ng_dismissed");
  }

  setCookie("ng_market", market, {
    maxAge: COOKIE_MAX_AGE,
  });

  setCookie("ng_lang", language, {
    maxAge: COOKIE_MAX_AGE,
  });
}

/**
 * Get the persisted checkout data from local storage
 */
export function getCheckoutLocalStorage(): CheckoutLocalStorage | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  if (!localStorage.getItem(CHECKOUT_STORAGE_KEY)) {
    return undefined;
  }

  return JSON.parse(localStorage.getItem(CHECKOUT_STORAGE_KEY) || "{}");
}

/**
 * Util function to delete all cookies related to the checkout and cart when an order is placed
 */
export function deleteCartCookies() {
  /**
   * @todo Uncomment this after the testing of Adyen checkout
   * This is temporarily commmented out to avoid deleting the address store when the order is placed,
   * which will save time by not having to re-enter the address when the order is placed.
   */
  // localStorage.removeItem(CHECKOUT_STORAGE_KEY);

  deleteCookie(CART_COOKIE);
  deleteCookie(GIFT_CARD_COOKIE);
}

/**
 * Create a new cart from an existing cart
 * Sometimes we need to call this when the cart for some reason is corrupted. for example during placement of an unsuccessful order.
 */
export async function createNewCartFromExistingCart({
  locale,
  newLocale,
}: {
  locale?: string;
  newLocale?: string;
}) {
  if (!locale) {
    throw new Error("No locale found");
  }

  const cartCookie = getCartCookie();
  const validation = z.string().safeParse(cartCookie?.value);

  if (!validation.success) {
    throw new Error("No cart cookie found");
  }

  const cart = await getCart({
    cartId: validation.data,
    locale,
  });

  if (!cart) {
    throw new Error("No cart found");
  }

  const items = cart.items.map(({ partNo, quantity }) => ({
    partNo,
    quantity,
  }));

  const newCart = await createNewCart({
    items,
    locale: newLocale ?? locale,
  });

  deleteCookie(CART_COOKIE);

  setCookie(CART_COOKIE, `${newCart.id}:${newCart.token}`, {
    maxAge: COOKIE_MAX_AGE,
  });
}

/**
 *
 * @param input Cart
 * @returns Stockwatch query variables
 */
export function cartToStockWatch(input?: Cart): StockWatchQueryVariables {
  if (!input) {
    return {
      items: [],
    };
  }

  const items = input.items.map((item) => ({
    partNo: item.partNo,
    quantity: item.quantity,
  }));

  return { items };
}

export function shouldShowEurobonus(
  cartSum: number,
  marketConfig: MarketConfiguration,
) {
  if (!FEATURE_FLAGS.checkout.eurobonus) return false;
  return (
    marketConfig.eurobonus &&
    marketConfig.eurobonusThreshold &&
    cartSum >= marketConfig.eurobonusThreshold
  );
}
